// Pixiwoo · product page extras (14/10/2026, fixed design bundle 15/10/2026)
// - [data-pxw-videos]  vertical video trio: muted autoplay while on screen, tap the speaker for sound
// - [data-pxw-rail]    collection rail: arrows appear only when the cards overflow
// - [data-pxw-fbt]     same-design bundle, two modes:
//     * fixed  — the family collection has a Bundle code prefix (metafield custom.bundle_code, e.g. LOOK20-GOLDEN).
//                One bundle per design, always all items: the design is picked on the ornament only and every
//                other product follows it (locked); sizes stay free (mug 11/15 oz, pillow 14–20 in), starting
//                from the default sizes (sizeHints). The button adds all items and applies the design's own code
//                <prefix>-<DESIGN-SLUG> (e.g. LOOK20-GOLDEN-PEARL-SNOWMAN), which takes the % off only that
//                design's variants (all sizes). Codes already on the cart are kept, so two design bundles both get it.
//     * free   — no prefix: the original tick-what-you-want bundle, no offer.
import { CartAddEvent, CartErrorEvent } from '@theme/events';

const esc = (s) =>
  String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
const norm = (s) => String(s == null ? '' : s).trim().toLowerCase();
const fill = (tpl, map) => String(tpl || '').replace(/\[(\w+)\]/g, (m, k) => (k in map ? map[k] : m));
const slug = (s) =>
  String(s || '')
    .toUpperCase()
    .replace(/['’]/g, '')
    .replace(/[^A-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
const reduceMotion = () => window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function formatMoney(cents, format) {
  const fmt = String(format || '${{amount}}').replace(/<[^>]*>/g, '');
  const value = Number(cents || 0) / 100;
  const delimit = (num, decimals, thousands = ',', decimal = '.') => {
    const parts = num.toFixed(decimals).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return parts.join(decimal);
  };
  return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
    switch (key) {
      case 'amount_no_decimals':
        return delimit(value, 0);
      case 'amount_with_comma_separator':
        return delimit(value, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator':
        return delimit(value, 0, '.', ',');
      default:
        return delimit(value, 2);
    }
  });
}

/* ---------------- video trio ---------------- */
function initVideos(root) {
  if (root.dataset.pxwInit) return;
  root.dataset.pxwInit = '1';
  const videos = [...root.querySelectorAll('video')];
  if (!videos.length) return;

  videos.forEach((v) => {
    v.muted = true;
    v.loop = true;
    v.playsInline = true;
    v.setAttribute('playsinline', '');
    v.removeAttribute('controls');
    v.removeAttribute('autoplay');
  });

  const setBtn = (btn, on) => {
    if (!btn) return;
    btn.setAttribute('aria-pressed', String(on));
    btn.setAttribute('aria-label', on ? 'Turn sound off' : 'Turn sound on');
  };

  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((entry) => {
          const v = entry.target;
          if (entry.isIntersecting && !reduceMotion()) {
            v.play().catch(() => {});
          } else if (!entry.isIntersecting) {
            v.pause();
          }
        }),
      { threshold: 0.35 }
    );
    videos.forEach((v) => io.observe(v));
  }

  root.addEventListener('click', (event) => {
    const item = event.target.closest('.pxw-pv__item');
    if (!item) return;
    const video = item.querySelector('video');
    const btn = item.querySelector('[data-pxw-sound]');
    if (!video) return;
    const turnOn = video.muted;
    videos.forEach((other) => {
      if (other === video) return;
      other.muted = true;
      setBtn(other.closest('.pxw-pv__item')?.querySelector('[data-pxw-sound]'), false);
    });
    video.muted = !turnOn;
    if (turnOn || video.paused) video.play().catch(() => {});
    setBtn(btn, turnOn);
  });
}

/* ---------------- collection rail ---------------- */
function initRail(root) {
  if (root.dataset.pxwInit) return;
  root.dataset.pxwInit = '1';
  const track = root.querySelector('[data-pxw-track]');
  const arrows = [...root.querySelectorAll('.pxw-fr__arrow')];
  if (!track || arrows.length < 2) return;

  const update = () => {
    const overflow = track.scrollWidth > track.clientWidth + 4;
    arrows.forEach((a) => (a.hidden = !overflow));
    if (!overflow) return;
    arrows[0].disabled = track.scrollLeft <= 2;
    arrows[1].disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
  };
  arrows.forEach((a) =>
    a.addEventListener('click', () => {
      track.scrollBy({ left: Number(a.dataset.dir) * track.clientWidth * 0.9, behavior: reduceMotion() ? 'auto' : 'smooth' });
    })
  );
  track.addEventListener('scroll', update, { passive: true });
  if ('ResizeObserver' in window) new ResizeObserver(update).observe(track);
  update();
}

/* ---------------- same-design bundle ---------------- */
function initFbt(root) {
  if (root.dataset.pxwInit) return;
  root.dataset.pxwInit = '1';

  let data;
  try {
    data = JSON.parse(root.querySelector('[data-fbt-json]').textContent);
  } catch (err) {
    console.error('[pxw-fbt] bad data', err);
    return;
  }
  const [cur, ...others] = data.products || [];
  if (!cur || !others.length) return;

  const el = {
    sub: root.querySelector('[data-fbt-sub]'),
    pics: root.querySelector('[data-fbt-pics]'),
    list: root.querySelector('[data-fbt-list]'),
    total: root.querySelector('[data-fbt-total]'),
    was: root.querySelector('[data-fbt-was]'),
    tag: root.querySelector('[data-fbt-tag]'),
    save: root.querySelector('[data-fbt-save]'),
    add: root.querySelector('[data-fbt-add]'),
    error: root.querySelector('[data-fbt-error]'),
  };
  const money = (c) => formatMoney(c, data.moneyFormat);
  const isSet = (v) => /set of/i.test(v.t || '');
  const byId = (p, id) => p.variants.find((v) => String(v.id) === String(id));
  // Variants a shopper can pick for a product: the page's product keeps all of them (incl. sets),
  // the others drop sold-out and "Set of N" variants.
  const poolOf = (p) => (p.current ? p.variants.filter((v) => v.a || String(v.id) === String(state.curId)) : p.variants.filter((v) => v.a && !isSet(v)));
  const locked = Boolean(data.currentLocked); // personalized page product: shown, never added from here
  const B = data.bundle || {};
  const bPct = Number(B.pct || 0);
  const bMin = Number(B.min || 3);
  const prefix = String(B.code || '').trim();
  const all = [cur, ...others];
  const fixed = bPct > 0 && Boolean(prefix) && !locked && data.mode === 'design' && all.length >= bMin && all.every((p) => p.optIndex >= 0);
  // The product whose design the shopper picks: the ornament (else the page's product).
  const master = all.find((p) => /ornament/i.test(p.type || '')) || cur;
  // Price after the bundle % (Shopify rounds each line to the cent).
  const dealPrice = (cents) => Number(cents || 0) - Math.round((Number(cents || 0) * bPct) / 100);
  // opts: per product, the non-design options the shopper picked in the bundle (e.g. { 1: "15 oz" }).
  const state = { curId: data.selected, picks: new Map(), on: new Map(), opts: new Map(), synced: undefined, design: null };
  others.forEach((p) => state.on.set(p.id, true));
  let rows = [];
  let deal = false;
  let dealCode = '';

  const curVariant = () => byId(cur, state.curId) || cur.variants[0];
  const designOf = (p, v) => (p.optIndex >= 0 && v ? v.o[p.optIndex] : null);

  // Default variant: the one whose title matches the most size hints (e.g. "11 oz", "18×18");
  // ties go to the first in Shopify's order. Hints and titles are compared without spaces.
  function pickDefault(list) {
    const squash = (s) => norm(s).replace(/\s+/g, '');
    const hints = (data.sizeHints || []).map(squash).filter(Boolean);
    let best = list[0];
    let bestScore = 0;
    for (const v of list) {
      const t = squash(v.t);
      const score = hints.reduce((n, h) => n + (t.includes(h) ? 1 : 0), 0);
      if (score > bestScore) {
        best = v;
        bestScore = score;
      }
    }
    return best;
  }

  // Variant of p in the given design, keeping the other options (size) of `prev` when possible.
  function matchDesign(p, design, prev) {
    const pool = poolOf(p);
    if (!pool.length) return null;
    let list = design ? pool.filter((v) => norm(v.o[p.optIndex]) === norm(design)) : [];
    if (!list.length) {
      const first = pool[0].o[p.optIndex];
      list = pool.filter((v) => v.o[p.optIndex] === first);
    }
    const keep = prev ? list.find((v) => v.o.every((x, i) => i === p.optIndex || x === prev.o[i])) : null;
    return keep || pickDefault(list);
  }

  /* ----- fixed bundle helpers ----- */
  // Variants of p in one design that can be bought (sizes etc. free).
  const designList = (p, design) => p.variants.filter((v) => v.a && !isSet(v) && norm(v.o[p.optIndex]) === norm(design));
  // The variant of p for a design: the sizes the shopper picked in the bundle, else the default sizes.
  function fixedVariant(p, design) {
    const list = designList(p, design);
    if (!list.length) return null;
    const want = state.opts.get(p.id) || {};
    const keys = Object.keys(want);
    const hit = keys.length ? list.find((v) => keys.every((k) => v.o[k] === want[k])) : null;
    return hit || pickDefault(list);
  }
  const masterDesigns = [...new Set(master.variants.filter((v) => v.a && !isSet(v)).map((v) => v.o[master.optIndex]))];

  // Free mode: the buy box is the master; every suggested product follows its design.
  function syncToBuyBox() {
    const cv = curVariant();
    const design = isSet(cv) ? null : designOf(cur, cv);
    if (design === state.synced) return;
    state.synced = design;
    others.forEach((p) => {
      const v = matchDesign(p, design, byId(p, state.picks.get(p.id)));
      if (v) state.picks.set(p.id, v.id);
    });
  }

  // Fixed mode: the bundle's design follows the buy box whenever the page's product is on a single design.
  function syncDesign() {
    const cv = curVariant();
    const d = isSet(cv) ? null : designOf(cur, cv);
    const hit = d ? masterDesigns.find((x) => norm(x) === norm(d)) : null;
    if (hit) state.design = hit;
    if (!state.design) state.design = masterDesigns[0] || null;
  }

  // One <select> per option (Design, Size, …). Prices appear in an option's labels only when
  // they differ between its values (so sizes show prices, designs don't).
  function selectsFor(p, v) {
    const pool = poolOf(p);
    return p.options
      .map((name, i) => {
        const values = [...new Set(pool.map((x) => x.o[i]))];
        const priceFor = (val) => {
          const hit =
            pool.find((x) => x.o[i] === val && x.o.every((y, j) => j === i || y === v.o[j])) || pool.find((x) => x.o[i] === val);
          return hit ? hit.p : null;
        };
        const prices = values.map(priceFor);
        const showPrice = new Set(prices).size > 1;
        if (values.length < 2) return values[0] && values[0] !== 'Default Title' ? `<span class="pxw-fbt__fixed">${esc(values[0])}</span>` : '';
        const opts = values
          .map((val, k) => {
            const label = showPrice && prices[k] != null ? `${val} · ${money(prices[k])}` : val;
            return `<option value="${esc(val)}"${val === v.o[i] ? ' selected' : ''}>${esc(label)}</option>`;
          })
          .join('');
        return `<select class="pxw-fbt__sel" data-fbt-pid="${p.id}" data-fbt-opt="${i}" aria-label="${esc(`${p.title}: ${name}`)}">${opts}</select>`;
      })
      .join('');
  }

  // Fixed mode: <select>s for the options other than design (size…), within the locked design.
  function sizeSelectsFor(p, v, design) {
    const list = designList(p, design);
    return p.options
      .map((name, i) => {
        if (i === p.optIndex) return '';
        const values = [...new Set(list.map((x) => x.o[i]))];
        if (values.length < 2) return values[0] && values[0] !== 'Default Title' ? `<span class="pxw-fbt__fixed">${esc(values[0])}</span>` : '';
        const priceFor = (val) => {
          const hit = list.find((x) => x.o[i] === val && x.o.every((y, j) => j === i || y === v.o[j])) || list.find((x) => x.o[i] === val);
          return hit ? hit.p : null;
        };
        const prices = values.map(priceFor);
        const showPrice = new Set(prices).size > 1;
        const opts = values
          .map((val, k) => {
            const label = showPrice && prices[k] != null ? `${val} · ${money(prices[k])}` : val;
            return `<option value="${esc(val)}"${val === v.o[i] ? ' selected' : ''}>${esc(label)}</option>`;
          })
          .join('');
        return `<select class="pxw-fbt__sel" data-fbt-size data-fbt-pid="${p.id}" data-fbt-opt="${i}" aria-label="${esc(`${p.title}: ${name}`)}">${opts}</select>`;
      })
      .join('');
  }

  function priceHtml(cents) {
    if (!deal) return esc(money(cents));
    return `<s>${esc(money(cents))}</s><ins>${esc(money(dealPrice(cents)))}</ins>`;
  }

  function picsHtml() {
    return rows
      .filter((r) => r.v)
      .map((r, i) => {
        const plus = i ? '<span class="pxw-fbt__plus" aria-hidden="true">+</span>' : '';
        const img = r.v.img
          ? `<img src="${esc(r.v.img)}" alt="${esc(`${r.p.title}, ${r.v.t}`)}" loading="lazy" width="480" height="480">`
          : '';
        const cls = `pxw-fbt__pic${r.on ? '' : ' is-off'}`;
        return r.p.current
          ? `${plus}<span class="${cls}">${img}</span>`
          : `${plus}<a class="${cls}" href="${esc(`${r.p.url}?variant=${r.v.id}`)}">${img}</a>`;
      })
      .join('');
  }

  function nameHtml(r) {
    const link = r.v ? `${r.p.url}?variant=${r.v.id}` : r.p.url;
    return `${r.p.current ? `<span class="pxw-fbt__this">${esc(data.thisLabel)}</span> ` : ''}${
      r.p.current ? esc(r.p.title) : `<a href="${esc(link)}">${esc(r.p.title)}</a>`
    }`;
  }

  function paintTotals(picked) {
    const full = picked.reduce((sum, r) => sum + Number(r.v.p || 0), 0);
    const dealTotal = picked.reduce((sum, r) => sum + dealPrice(r.v.p), 0);
    const vars = { n: picked.length, pct: bPct };
    el.total.textContent = money(deal ? dealTotal : full);
    if (el.was) {
      el.was.hidden = !deal;
      el.was.textContent = deal ? money(full) : '';
    }
    if (el.tag) {
      el.tag.hidden = !deal;
      el.tag.textContent = deal ? fill(B.tag || '−[pct]%', vars) : '';
    }
    if (el.save) {
      el.save.hidden = !deal;
      el.save.classList.remove('is-hint');
      el.save.textContent = deal ? fill(B.save || 'You save [amount]', { ...vars, amount: money(full - dealTotal) }) : '';
    }
    el.add.textContent = deal
      ? fill(B.addLabel || data.addLabel || 'Add all [n] to cart', vars)
      : picked.length > 1
        ? String(data.addLabel || 'Add all [n] to cart').replace('[n]', picked.length)
        : data.addOneLabel || 'Add to cart';
  }

  /* ----- fixed bundle: design picked on the ornament, everything follows; sizes free ----- */
  function renderFixed() {
    syncDesign();
    const cv = curVariant();
    const design = state.design;
    rows = all.map((p) => {
      // The page's own product keeps the variant (size) chosen in the buy box when it is on this design.
      const own = p.current && !isSet(cv) && cv.a && norm(designOf(cur, cv)) === norm(design) ? cv : null;
      return { p, v: own || fixedVariant(p, design), on: true };
    });
    const complete = rows.every((r) => r.v);
    deal = complete;
    dealCode = complete ? `${prefix}-${slug(design)}` : '';
    root.classList.toggle('is-deal', deal);
    root.classList.add('is-fixed');

    if (el.sub) {
      el.sub.textContent = design ? String(data.subLabel || '').replace('[design]', design) : '';
      el.sub.hidden = !el.sub.textContent;
    }
    el.pics.innerHTML = picsHtml();
    el.list.innerHTML = rows
      .map((r) => {
        let pick;
        if (!r.v) {
          pick = `<span class="pxw-fbt__var pxw-fbt__locked">${esc(`Not available in ${design}`)}</span>`;
        } else if (r.p === master) {
          const opts = masterDesigns.map((d) => `<option value="${esc(d)}"${norm(d) === norm(design) ? ' selected' : ''}>${esc(d)}</option>`).join('');
          pick = `<div class="pxw-fbt__opts"><select class="pxw-fbt__sel" data-fbt-master aria-label="${esc(`${r.p.title}: ${r.p.options[r.p.optIndex]}`)}">${opts}</select>${sizeSelectsFor(r.p, r.v, design)}</div>`;
        } else {
          pick = `<span class="pxw-fbt__var">${esc(r.v.o[r.p.optIndex])}</span><div class="pxw-fbt__opts">${sizeSelectsFor(r.p, r.v, design)}</div>`;
        }
        return `<li class="pxw-fbt__row pxw-fbt__row--fixed${r.v ? '' : ' is-off'}">
          <span class="pxw-fbt__dot" aria-hidden="true"></span>
          <div class="pxw-fbt__name">${nameHtml(r)}${pick}</div>
          <span class="pxw-fbt__price">${r.v ? priceHtml(r.v.p) : ''}</span>
        </li>`;
      })
      .join('');
    const picked = rows.filter((r) => r.v);
    paintTotals(picked);
    el.add.disabled = picked.length === 0;
    root.hidden = false;
  }

  /* ----- free bundle: tick what you want, no offer ----- */
  function renderFree() {
    syncToBuyBox();
    const cv = curVariant();
    const design = designOf(cur, cv);
    rows = [{ p: cur, v: cv, on: !locked }];
    others.forEach((p) => {
      const v = byId(p, state.picks.get(p.id));
      if (v) rows.push({ p, v, on: state.on.get(p.id) !== false });
    });
    deal = false;
    dealCode = '';

    if (el.sub) {
      const follows = data.mode === 'design' || others.some((p) => p.optIndex >= 0 && p.options[p.optIndex] === data.optName);
      el.sub.textContent = follows && design && !isSet(cv) ? String(data.subLabel || '').replace('[design]', design) : '';
      el.sub.hidden = !el.sub.textContent;
    }
    el.pics.innerHTML = picsHtml();
    el.list.innerHTML = rows
      .map((r) => {
        const id = `pxw-fbt-${r.p.id}`;
        const isLocked = r.p.current && locked;
        const note = isLocked && data.lockedNote ? `<span class="pxw-fbt__var pxw-fbt__locked">${esc(data.lockedNote)}</span>` : '';
        return `<li class="pxw-fbt__row${r.on ? '' : ' is-off'}${isLocked ? ' is-locked' : ''}">
          <input type="checkbox" id="${id}" data-fbt-on="${r.p.id}"${r.on ? ' checked' : ''}${r.p.current ? ' disabled' : ''} aria-label="${esc(r.p.title)}">
          <div class="pxw-fbt__name"><label for="${id}">${nameHtml(r)}</label>${note}<div class="pxw-fbt__opts">${selectsFor(r.p, r.v)}</div></div>
          <span class="pxw-fbt__price">${priceHtml(r.v.p)}</span>
        </li>`;
      })
      .join('');
    const picked = rows.filter((r) => r.on);
    paintTotals(picked);
    el.add.disabled = picked.length === 0 || (!locked && !cv.a);
    root.hidden = false;
  }

  const render = () => (fixed ? renderFixed() : renderFree());

  // Changing the page's own product drives the buy box (same radios the shopper would click),
  // so the gallery, price and Add to cart above stay in step. Falls back to a local change if the picker isn't there.
  function pickCurrent(optIndex, value) {
    const picker = document.querySelector(`variant-picker[data-product-id="${cur.id}"]`);
    const input = picker
      ? [...picker.querySelectorAll(`fieldset[data-fieldset-index="${optIndex}"] input[type="radio"]`)].find((x) => x.value === value)
      : null;
    if (input) {
      input.click();
      return true;
    }
    const cv = curVariant();
    const pool = poolOf(cur).filter((v) => v.o[optIndex] === value);
    const next = pool.find((v) => v.o.every((x, i) => i === optIndex || x === cv.o[i])) || pool[0];
    if (next) {
      state.curId = next.id;
      render();
    }
    return false;
  }

  root.addEventListener('change', (event) => {
    const t = event.target;
    if (t.matches('[data-fbt-master]')) {
      // Fixed mode: the shopper picked the ornament's design. Everything follows; the buy box too.
      state.design = t.value;
      const cv = curVariant();
      const sameNow = !isSet(cv) && norm(designOf(cur, cv)) === norm(t.value);
      if (cur.optIndex >= 0 && !sameNow) {
        const hit = poolOf(cur).find((v) => norm(v.o[cur.optIndex]) === norm(t.value));
        if (hit) pickCurrent(cur.optIndex, hit.o[cur.optIndex]);
      }
      render();
      return;
    }
    if (t.matches('[data-fbt-size]')) {
      // Fixed mode: a size (or other non-design option) of one item. The page's own product moves the buy box.
      const pid = Number(t.dataset.fbtPid);
      const optIndex = Number(t.dataset.fbtOpt);
      const want = { ...(state.opts.get(pid) || {}), [optIndex]: t.value };
      state.opts.set(pid, want);
      if (String(pid) === String(cur.id)) {
        pickCurrent(optIndex, t.value);
        return;
      }
      render();
      return;
    }
    if (t.matches('[data-fbt-on]')) {
      state.on.set(Number(t.dataset.fbtOn), t.checked);
      render();
      return;
    }
    if (!t.matches('[data-fbt-opt]')) return;
    const pid = Number(t.dataset.fbtPid);
    const optIndex = Number(t.dataset.fbtOpt);
    if (String(pid) === String(cur.id)) {
      pickCurrent(optIndex, t.value);
      return;
    }
    const p = others.find((x) => x.id === pid);
    if (!p) return;
    const prev = byId(p, state.picks.get(pid));
    const pool = poolOf(p).filter((v) => v.o[optIndex] === t.value);
    const next = (prev && pool.find((v) => v.o.every((x, i) => i === optIndex || x === prev.o[i]))) || pickDefault(pool);
    if (next) state.picks.set(pid, next.id);
    render();
  });

  document.addEventListener('variant:update', (event) => {
    const d = event.detail || {};
    if (String(d.data?.productId || '') !== String(cur.id)) return;
    if (!d.resource || !d.resource.id) return;
    state.curId = d.resource.id;
    render();
  });

  const jsonPost = (url, body) =>
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });

  el.add.addEventListener('click', async () => {
    const items = rows.filter((r) => r.on && r.v).map((r) => ({ id: Number(r.v.id), quantity: 1 }));
    if (!items.length || el.add.classList.contains('is-busy')) return;
    const code = deal ? dealCode : '';
    el.add.classList.add('is-busy');
    el.add.setAttribute('aria-busy', 'true');
    el.error.hidden = true;

    const sectionIds = [...document.querySelectorAll('cart-items-component')].map((c) => c.dataset.sectionId).filter(Boolean);
    const body = { items };
    if (sectionIds.length) body.sections = sectionIds.join(',');
    const addUrl = `${String(data.cartAddUrl || '/cart/add').replace(/\.js$/, '')}.js`;
    const updateUrl = `${String(data.cartUpdateUrl || '/cart/update').replace(/\.js$/, '')}.js`;
    const cartUrl = data.cartUrl || '/cart';
    const getCart = () =>
      fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } })
        .then((r) => r.json())
        .catch(() => undefined);

    try {
      const res = await jsonPost(addUrl, body);
      const json = await res.json();
      if (!res.ok || json.status) {
        el.error.textContent = json.description || json.message || 'Could not add to cart.';
        el.error.hidden = false;
        document.dispatchEvent(new CartErrorEvent('pxw-fbt', json.message, json.description, json.errors));
        return;
      }
      let sections = json.sections;
      let cart = await getCart();
      if (code) {
        // Keep the codes already working on the cart (e.g. another design's bundle) and add this design's code.
        const keep = ((cart && cart.discount_codes) || []).filter((d) => d.applicable).map((d) => d.code);
        const codes = [...new Set([...keep, code])];
        const upd = { discount: codes.join(',') };
        if (sectionIds.length) upd.sections = sectionIds.join(',');
        try {
          const r2 = await jsonPost(updateUrl, upd);
          const j2 = await r2.json();
          if (r2.ok && !j2.status) {
            if (j2.sections) sections = j2.sections;
            if (Array.isArray(j2.items)) cart = j2;
          }
        } catch (err) {
          console.warn('[pxw-fbt] bundle code', err);
        }
      }
      root.dispatchEvent(
        new CartAddEvent(cart, `pxw-fbt-${cur.id}`, {
          source: 'pxw-fbt',
          itemCount: items.length,
          variantId: String(items[0].id),
          sections,
        })
      );
      const drawer = document.querySelector('cart-drawer-component');
      if (!drawer) {
        window.location.href = cartUrl;
        return;
      }
      if (!drawer.hasAttribute('auto-open') && typeof drawer.open === 'function') drawer.open();
    } catch (err) {
      console.error('[pxw-fbt] add to cart', err);
      el.error.textContent = 'Could not add to cart. Please try again.';
      el.error.hidden = false;
    } finally {
      el.add.classList.remove('is-busy');
      el.add.removeAttribute('aria-busy');
    }
  });

  render();
}

function initAll(scope = document) {
  scope.querySelectorAll('[data-pxw-videos]').forEach(initVideos);
  scope.querySelectorAll('[data-pxw-rail]').forEach(initRail);
  scope.querySelectorAll('[data-pxw-fbt]').forEach(initFbt);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initAll());
} else {
  initAll();
}
document.addEventListener('shopify:section:load', (event) => initAll(event.target));
